import classNames from "classnames";
import { useGrowl } from "PFApp/use_growl";
import { RoleAvailability } from "PFComponents/availability";
import { Button } from "PFComponents/button";
import Checkbox from "PFComponents/checkbox/checkbox";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import Toggle from "PFComponents/toggle/toggle";
import { useRoleName } from "PFCore/helpers/activities";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import useStorage from "PFCore/helpers/use_storage";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { confirmRole } from "./api";
import { AuditRoleStatus } from "./audit_role_status/audit_role_status";
import { isOverbooked, isRoleDisabled, tabByWorkflowState } from "./helpers";
import Resource from "./resource";
import RolesActions from "./roles_actions";
import css from "./roles_list.module.scss";
import TypeTooltipIcon from "./type_tooltip_icon";
/* eslint-disable react/display-name */

const Grade = ({ role }) => role.grade;
const RoleName = ({ role, engagement }) => {
  const getRoleName = useRoleName();

  return isRoleDisabled(role) ? (
    <span>{getRoleName(role)}</span>
  ) : (
    <Link
      to={`/audit_planner/${tabByWorkflowState[role.workflow_state] || "control_centre"}/${engagement.id}/${
        role.id
      }`}
    >
      {getRoleName(role)}
    </Link>
  );
};

RoleName.propTypes = {
  engagement: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired
};

const ConfirmModeBar = ({ rolesSelected, selectBookedRoles, selectNoRoles, onSuccess, onFailure }) => {
  const growl = useGrowl();
  const { t } = useTranslation("auditPlanner");
  const { getFirstError } = useResponseErrors();
  const [displayConfirmModal, setDisplayConfirmModal] = useState(false);
  const confirmBookings = () =>
    Promise.all(rolesSelected.map(confirmRole))
      .then(() => {
        if (onSuccess) {
          onSuccess();
        }
        setDisplayConfirmModal(false);
        growl({
          kind: "success",
          message: t("parts.successfullyConfirmedBookings", { count: rolesSelected.length })
        });
      })
      .catch((response) => {
        if (onFailure) {
          onFailure();
        }
        setDisplayConfirmModal(false);
        const error = getFirstError(response);
        growl({
          message: error || t("parts.errorConfirmingBookings"),
          kind: "error"
        });
      });

  return (
    <div className={css.confirmBar}>
      <span className={css.selectButtons}>
        <span>{`${t("selectColon")} `}</span>
        <Button className={css.buttonBlank} kind="blank" onClick={selectBookedRoles}>
          {t("all")}
        </Button>
        <Button className={css.buttonBlank} kind="blank" onClick={selectNoRoles}>
          {t("none")}
        </Button>
      </span>
      <span>{t("parts.countRolesSelected", { count: rolesSelected.length })}</span>
      <span>
        <Button
          text={t("parts.confirmBookings")}
          disabled={rolesSelected.length === 0}
          onClick={() => setDisplayConfirmModal(true)}
        />
      </span>
      {displayConfirmModal && (
        <ConfirmModal
          handleOK={confirmBookings}
          handleClose={() => {
            setDisplayConfirmModal(false);
          }}
          style={{ textAlign: "center" }}
          title={t("parts.confirmABooking")}
        >
          {t("parts.sureToConfirmBooking")}
        </ConfirmModal>
      )}
    </div>
  );
};

ConfirmModeBar.propTypes = {
  rolesSelected: PropTypes.arrayOf(PropTypes.number),
  selectBookedRoles: PropTypes.func,
  selectNoRoles: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired
};

const RolesList = ({
  items,
  tab,
  confirmModeEnabled,
  setConfirmModeEnabled,
  columns,
  engagement,
  fetchAllEngagements
}) => {
  const { t } = useTranslation("auditPlanner");
  const [reviewedShortlistIds, setReviewedShortlistIds] = useStorage(`audit_role_reviewd_shortlists`, [], {
    expiresIn: 60 * 60 * 72
  });

  const canConfirm = useCallback(
    (role) => {
      if (!(role.workflow_state === "booked")) {
        return false;
      }
      const shortlist = role.shortlists && role.shortlists.find(({ state }) => state === "booked");
      if (!shortlist) {
        return false;
      }
      return (
        shortlist.state === "booked" &&
        (isOverbooked(role) ? reviewedShortlistIds.includes(shortlist.id) : true)
      );
    },
    [reviewedShortlistIds]
  );

  const hasRolesToConfirm = !!items.find(({ activity }) => canConfirm(activity));

  const allowConfirmMode = tab === "control_centre" && hasRolesToConfirm;
  const [rolesSelected, setRolesSelected] = useState([]);

  const defaultColumns = [
    {
      header: t("parts.headers.roleName"),
      Component: RoleName,
      cellStyle: {
        maxWidth: "20%",
        textOverflow: "ellipsis",
        wordBreak: "break-word",
        paddingLeft: 0,
        position: "relative"
      },
      headerStyle: { width: "20%", paddingLeft: 0 }
    },
    {
      header: t("parts.headers.grade"),
      Component: Grade,
      cellStyle: {
        maxWidth: "15%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      },
      headerStyle: { width: "15%" }
    },
    {
      header: t("parts.headers.duration"),
      Component: RoleAvailability,
      cellStyle: {
        whiteSpace: "nowrap"
      }
    },
    {
      header: t("parts.headers.type"),
      tabs: ["exceptions"],
      Component: TypeTooltipIcon,
      cellStyle: { width: "8%", whiteSpace: "nowrap" },
      headerStyle: { width: "8%" }
    },
    {
      header: t("parts.headers.resource"),
      Component: Resource,
      headerStyle: { width: "15%" },
      cellStyle: { maxWidth: "15%", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }
    },
    {
      header: "",
      tabs: ["control_centre"],
      cellStyle: { width: "1%", textAlign: "end", paddingRight: 0 },
      headerStyle: { width: "1%", paddingRight: 0 },
      Component: AuditRoleStatus
    },
    {
      header: "",
      tabs: ["control_centre"],
      cellStyle: { width: "40px", textAlign: "end", paddingRight: 0 },
      headerStyle: { width: "40px", paddingRight: 0 },
      Component: RolesActions
    }
  ];

  const columnsToShow = useMemo(
    () => defaultColumns.filter((col) => !col.tabs || col.tabs.includes(tab)).concat(columns || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const selectBookedRoles = useCallback(
    () =>
      setRolesSelected(
        items
          .map(
            ({ activity }) =>
              canConfirm(activity) && activity.shortlists.find(({ state }) => state === "booked")?.id
          )
          .filter(Boolean)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, reviewedShortlistIds]
  );

  const selectNoRoles = useCallback(() => setRolesSelected([]), []);

  const onBooked = useCallback(() => {
    selectNoRoles();
    fetchAllEngagements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {allowConfirmMode && confirmModeEnabled && (
        <>
          <div className={classNames(css.confirmToggle, { [css.enabled]: confirmModeEnabled })}>
            <div className={css.confirmToggleEnabled}>
              <Toggle
                inline
                label={t("parts.confirmMode")}
                checked={confirmModeEnabled}
                onChange={() => setConfirmModeEnabled(!confirmModeEnabled)}
              />
            </div>
          </div>
          <ConfirmModeBar
            rolesSelected={rolesSelected}
            selectBookedRoles={selectBookedRoles}
            selectNoRoles={selectNoRoles}
            onSuccess={onBooked}
            onFailure={fetchAllEngagements}
          />
        </>
      )}
      <div className={css.table}>
        <div className={css.header}>
          {columnsToShow.map((col, index) => (
            <div key={index} className={css.cell} style={col.headerStyle}>
              {col.header}
              {tab === "control_centre" && index === columnsToShow.length - 2 && (
                <div
                  className={classNames(css.confirmToggleDisabled)}
                  style={{ visibility: confirmModeEnabled || !hasRolesToConfirm ? "hidden" : "visible" }}
                >
                  <Toggle
                    inline
                    label={t("parts.confirmMode")}
                    checked={confirmModeEnabled}
                    onChange={() => setConfirmModeEnabled(!confirmModeEnabled)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {items.map(({ activity }) => (
          <div
            key={activity.id}
            className={classNames(css.row, {
              [css.disabled]: confirmModeEnabled && activity.workflow_state !== "booked"
            })}
          >
            {columnsToShow.map((col, index) => {
              const shortlist = activity.shortlists.find(({ state }) => state === "booked");
              const shortlistId = shortlist?.id;

              return (
                <div key={index} className={css.cell} style={col.cellStyle}>
                  {index === 0 && confirmModeEnabled && canConfirm(activity) && (
                    <div className={css.checkbox}>
                      <Checkbox
                        handleChange={(checked) =>
                          setRolesSelected(
                            checked
                              ? [...rolesSelected, shortlistId]
                              : rolesSelected.filter((value) => value !== shortlistId)
                          )
                        }
                        checked={rolesSelected.includes(shortlistId)}
                      />
                    </div>
                  )}
                  {col.Component && (
                    <col.Component
                      role={activity}
                      engagement={engagement}
                      reload={fetchAllEngagements}
                      status={activity.workflow_state}
                      reviewedShortlistIds={reviewedShortlistIds}
                      setReviewedShortlistIds={setReviewedShortlistIds}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className={css.space} />
      {confirmModeEnabled && (
        <ConfirmModeBar
          rolesSelected={rolesSelected}
          selectBookedRoles={selectBookedRoles}
          selectNoRoles={selectNoRoles}
          onSuccess={onBooked}
          onFailure={fetchAllEngagements}
        />
      )}
    </>
  );
};

RolesList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  tab: PropTypes.string,
  confirmModeEnabled: PropTypes.bool.isRequired,
  setConfirmModeEnabled: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      Component: PropTypes.func.isRequired,
      cellStyle: PropTypes.object
    })
  ),
  engagement: PropTypes.object.isRequired,
  fetchAllEngagements: PropTypes.func.isRequired
};

export default RolesList;
